import { useState, useEffect, useRef } from 'react'
import { nomeFormato } from '../lib/formati'

/**
 * Timer di allenamento per i formati usati nelle sessioni di gruppo.
 *
 * Costruisce una "scaletta" di fasi (lavoro/pausa/preparazione) a partire dal formato
 * del blocco e dalle impostazioni scelte dal coach, poi la esegue con un countdown.
 *
 * Formati supportati:
 *   emom     -> ogni minuto parte un nuovo round; gli esercizi ruotano
 *   tabata   -> 8 round da 20s lavoro / 10s pausa, per ciascun esercizio
 *   amrap    -> un unico countdown della durata totale
 *   circuit  -> N giri; dentro ogni giro un turno per esercizio, pausa tra i giri
 *   a_tempo  -> X sec lavoro / Y sec pausa per ogni esercizio
 *   classico -> non usa la scaletta: timer di solo recupero tra le serie
 */

const PREPARAZIONE_SEC = 10

function formattaTempo(sec) {
  const m = Math.floor(Math.abs(sec) / 60)
  const s = Math.abs(sec) % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/** Segnale acustico generato via Web Audio: nessun file audio da caricare. */
function useBeep() {
  const ctxRef = useRef(null)
  return (frequenza = 880, durataMs = 150) => {
    try {
      if (!ctxRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        if (!AudioCtx) return
        ctxRef.current = new AudioCtx()
      }
      const ctx = ctxRef.current
      if (ctx.state === 'suspended') ctx.resume()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = frequenza
      gain.gain.setValueAtTime(0.0001, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durataMs / 1000)
      osc.connect(gain); gain.connect(ctx.destination)
      osc.start(); osc.stop(ctx.currentTime + durataMs / 1000)
    } catch { /* audio non disponibile: il timer funziona comunque */ }
  }
}

/** Traduce blocco + impostazioni nella sequenza di fasi da eseguire. */
function costruisciFasi(blocco) {
  const imp = blocco.impostazioni || {}
  const esercizi = (blocco.esercizi || []).map(e => e.nomeEsercizio || e.nome || 'Esercizio')
  if (esercizi.length === 0) return []
  const fasi = [{ tipo: 'prep', durata: PREPARAZIONE_SEC, etichetta: 'Preparati', esercizio: esercizi[0] }]

  if (blocco.formato === 'emom') {
    // Nell'EMOM il minuto è la cadenza, non la durata del lavoro: si esegue, e
    // quel che avanza del minuto è recupero. Prima ogni minuto era un unico
    // blocco di 60 secondi di "lavoro", quindi un Plank da 30s diventava un
    // plank continuo da un minuto e il recupero spariva.
    const minuti = imp.durata || 10
    const lista = blocco.esercizi || []
    for (let m = 0; m < minuti; m++) {
      const ex = lista[m % lista.length]
      const lavoro = ex?.tipo_misura === 'tempo'
        ? Math.min(60, Math.max(1, ex.durata || 60))
        : 60
      fasi.push({ tipo: 'lavoro', durata: lavoro, etichetta: `Minuto ${m + 1} di ${minuti}`, esercizio: esercizi[m % esercizi.length] })
      if (lavoro < 60) {
        fasi.push({ tipo: 'pausa', durata: 60 - lavoro, etichetta: `Recupero fino al minuto ${m + 2}`, esercizio: 'Recupero' })
      }
    }
  } else if (blocco.formato === 'tabata') {
    const lavoro = imp.lavoro || 20, pausa = imp.pausa || 10, round = imp.round || 8
    esercizi.forEach((ex, ei) => {
      for (let r = 0; r < round; r++) {
        fasi.push({ tipo: 'lavoro', durata: lavoro, etichetta: `Round ${r + 1} di ${round}`, esercizio: ex })
        const ultimo = ei === esercizi.length - 1 && r === round - 1
        if (!ultimo) fasi.push({ tipo: 'pausa', durata: pausa, etichetta: 'Pausa', esercizio: ex })
      }
    })
  } else if (blocco.formato === 'amrap') {
    const minuti = imp.durata || 10
    fasi.push({ tipo: 'lavoro', durata: minuti * 60, etichetta: `AMRAP ${minuti} min — più giri possibili`, esercizio: esercizi.join(' → ') })
  } else if (blocco.formato === 'circuit') {
    const giri = imp.giri || 3, pausa = imp.pausa || 60, lavoro = imp.lavoro || 40
    for (let g = 0; g < giri; g++) {
      esercizi.forEach(ex => {
        fasi.push({ tipo: 'lavoro', durata: lavoro, etichetta: `Giro ${g + 1} di ${giri}`, esercizio: ex })
      })
      if (g < giri - 1) fasi.push({ tipo: 'pausa', durata: pausa, etichetta: `Pausa fine giro ${g + 1}`, esercizio: 'Recupero' })
    }
  } else if (blocco.formato === 'a_tempo') {
    // I giri mancavano: la scaletta passava una volta sola sugli esercizi, quindi
    // un blocco con un solo Plank da 40s finiva dopo 40 secondi. Ora la lista si
    // ripete quante volte dice il blocco, con la pausa fra una volta e l'altra.
    const lavoro = imp.lavoro || 40, pausa = imp.pausa || 20
    const giri = Math.max(1, imp.giri || 1)
    for (let g = 0; g < giri; g++) {
      esercizi.forEach((ex, i) => {
        const dove = esercizi.length > 1 ? `esercizio ${i + 1} di ${esercizi.length}` : ''
        const etichetta = giri > 1
          ? [`Giro ${g + 1} di ${giri}`, dove].filter(Boolean).join(' · ')
          : `Esercizio ${i + 1} di ${esercizi.length}`
        fasi.push({ tipo: 'lavoro', durata: lavoro, etichetta, esercizio: ex })
        const ultimo = g === giri - 1 && i === esercizi.length - 1
        if (!ultimo) fasi.push({ tipo: 'pausa', durata: pausa, etichetta: 'Pausa', esercizio: 'Recupero' })
      })
    }
  }
  return fasi
}

const COLORI = {
  prep: { bg: 'rgba(255,200,0,0.12)', bordo: 'rgba(255,200,0,0.4)', testo: 'var(--accent2)' },
  lavoro: { bg: 'rgba(127,214,61,0.12)', bordo: 'rgba(127,214,61,0.45)', testo: 'var(--accent)' },
  pausa: { bg: 'rgba(120,150,255,0.12)', bordo: 'rgba(120,150,255,0.4)', testo: 'var(--accent2)' },
}

export default function TimerBlocco({ blocco, onChiudi }) {
  const beep = useBeep()
  const fasi = useRef(costruisciFasi(blocco)).current
  const [idx, setIdx] = useState(0)
  const [rimanenti, setRimanenti] = useState(fasi[0]?.durata || 0)
  const [attivo, setAttivo] = useState(false)
  const [finito, setFinito] = useState(false)

  const fase = fasi[idx]

  // Tick del countdown: passa alla fase successiva quando arriva a zero.
  useEffect(() => {
    if (!attivo || finito) return
    const t = setInterval(() => {
      setRimanenti(r => {
        if (r > 1) {
          if (r <= 4) beep(660, 90) // conto alla rovescia degli ultimi 3 secondi
          return r - 1
        }
        // fine fase
        setIdx(i => {
          const prossimo = i + 1
          if (prossimo >= fasi.length) {
            setAttivo(false); setFinito(true)
            beep(1200, 500)
            return i
          }
          beep(fasi[prossimo].tipo === 'lavoro' ? 1000 : 520, 250)
          setRimanenti(fasi[prossimo].durata)
          return prossimo
        })
        return 0
      })
    }, 1000)
    return () => clearInterval(t)
  }, [attivo, finito, fasi, beep])

  // Mantiene lo schermo acceso durante l'allenamento, dove supportato.
  useEffect(() => {
    let sentinella = null
    if (attivo && 'wakeLock' in navigator) {
      navigator.wakeLock.request('screen').then(s => { sentinella = s }).catch(() => {})
    }
    return () => { if (sentinella) sentinella.release().catch(() => {}) }
  }, [attivo])

  function vaiA(nuovoIdx) {
    const i = Math.max(0, Math.min(fasi.length - 1, nuovoIdx))
    setIdx(i); setRimanenti(fasi[i].durata); setFinito(false)
  }

  function riavvia() { setIdx(0); setRimanenti(fasi[0].durata); setAttivo(false); setFinito(false) }

  if (fasi.length === 0) return null

  const colore = COLORI[fase.tipo] || COLORI.lavoro
  // Ultimi 3 secondi: il tempo lampeggia, così il conto alla rovescia si coglie
  // anche con la coda dell'occhio, senza dover fissare lo schermo.
  const ultimiSecondi = attivo && rimanenti <= 3 && rimanenti > 0
  const totaleFasi = fasi.filter(f => f.tipo !== 'prep').length
  const fatteFasi = fasi.slice(0, idx).filter(f => f.tipo !== 'prep').length
  const progresso = totaleFasi > 0 ? Math.round((fatteFasi / totaleFasi) * 100) : 0

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000, background: 'var(--bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <button onClick={onChiudi} aria-label="Chiudi timer"
        style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: '1px solid var(--border)', borderRadius: 999, width: 38, height: 38, color: 'var(--muted)', fontSize: 18, cursor: 'pointer' }}>✕</button>

      <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>
        {blocco.nome || 'Blocco'} · {nomeFormato(blocco.formato)}
      </div>

      {finito ? (
        <>
          <div style={{ fontSize: 60, margin: '10px 0' }}>🎉</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, marginBottom: 6 }}>Blocco completato!</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24, textAlign: 'center' }}>
            Ricordati di registrare i risultati degli esercizi.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={riavvia}>↻ Ripeti</button>
            <button className="btn btn-primary" onClick={onChiudi}>✓ Chiudi</button>
          </div>
        </>
      ) : (
        <>
          {/* Il telefono sta a terra o su una panca mentre ci si allena: il tempo
              deve leggersi a distanza, quindi occupa quanto più schermo possibile.
              clamp + min(vw,vh) lo fa scalare col dispositivo e regge anche in orizzontale. */}
          <div style={{
            width: '100%', maxWidth: 560, borderRadius: 20, padding: '24px 20px', textAlign: 'center',
            background: colore.bg,
            border: `2px solid ${ultimiSecondi ? colore.testo : colore.bordo}`,
            transition: 'border-color 0.15s',
          }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, letterSpacing: 1.5, color: colore.testo, textTransform: 'uppercase' }}>
              {fase.tipo === 'prep' ? 'Preparati' : fase.tipo === 'pausa' ? 'Pausa' : 'Lavoro'}
            </div>
            <div style={{
              // Instrument Sans, non Syne: le cifre di Syne in ExtraBold sono più larghe
              // che alte ("0:24" misurava 484px contro i ~398px utili di un telefono, quindi
              // usciva dallo schermo). Instrument Sans sta in 289px ed è pure più alto.
              fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600,
              fontSize: 'clamp(76px, min(32vw, 26vh), 200px)',
              lineHeight: 1, color: colore.testo, margin: '10px 0',
              fontVariantNumeric: 'tabular-nums', // evita che le cifre "ballino" cambiando
              opacity: ultimiSecondi && rimanenti % 2 === 0 ? 0.45 : 1,
              transition: 'opacity 0.12s',
            }}>
              {formattaTempo(rimanenti)}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{fase.esercizio}</div>
            <div style={{ fontSize: 15, color: 'var(--muted)' }}>{fase.etichetta}</div>
          </div>

          <div style={{ width: '100%', maxWidth: 560, marginTop: 14 }}>
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${progresso}%`, background: 'var(--accent)' }} /></div>
            <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 4 }}>{fatteFasi} / {totaleFasi} fasi</div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 22, alignItems: 'center' }}>
            <button onClick={() => vaiA(idx - 1)} disabled={idx === 0}
              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 12, width: 48, height: 48, fontSize: 18, color: idx === 0 ? 'var(--border)' : 'var(--text)', cursor: idx === 0 ? 'default' : 'pointer' }}>⏮</button>
            <button className="btn btn-primary" onClick={() => setAttivo(a => !a)} style={{ minWidth: 130, fontSize: 16, padding: '14px 20px' }}>
              {attivo ? '⏸ Pausa' : '▶ Avvia'}
            </button>
            <button onClick={() => vaiA(idx + 1)} disabled={idx >= fasi.length - 1}
              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 12, width: 48, height: 48, fontSize: 18, color: idx >= fasi.length - 1 ? 'var(--border)' : 'var(--text)', cursor: idx >= fasi.length - 1 ? 'default' : 'pointer' }}>⏭</button>
          </div>

          <button onClick={riavvia} style={{ marginTop: 14, background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
            ↻ Ricomincia il blocco
          </button>
        </>
      )}
    </div>
  )
}

/**
 * Timer di UNA serie a tempo: 10 secondi per prepararsi, poi i secondi
 * dell'esercizio. Alla fine la serie viene registrata da sola.
 *
 * Nasce da un difetto di impostazione: il timer del blocco e la registrazione
 * dei risultati erano due mondi separati. Si poteva spuntare una serie senza
 * aver avviato nulla, e si poteva eseguire tutto il blocco col cronometro senza
 * che restasse traccia di niente — alla fine compariva solo "ricordati di
 * registrare i risultati". Qui le due cose sono la stessa azione: chi finisce
 * il countdown ha la serie già salvata, con la durata davvero eseguita.
 */
export function TimerSerie({ secondi, nomeEsercizio, serieNumero, seriePreviste, onCompletata, onAnnulla }) {
  const beep = useBeep()
  const durata = Math.max(1, Math.round(secondi) || 1)
  const [fase, setFase] = useState('prep')          // 'prep' | 'lavoro' | 'fatta'
  const [rimanenti, setRimanenti] = useState(PREPARAZIONE_SEC)
  // La chiamata di fine deve partire UNA volta sola: senza questo, un nuovo
  // render mentre il countdown è a zero la farebbe ripartire e salverebbe
  // la stessa serie più volte.
  const giaCompletata = useRef(false)
  const rifCompletata = useRef(onCompletata)
  rifCompletata.current = onCompletata

  useEffect(() => {
    if (fase === 'fatta') return
    if (rimanenti > 0) {
      const t = setTimeout(() => {
        if (rimanenti <= 4) beep(660, 90)
        setRimanenti(r => r - 1)
      }, 1000)
      return () => clearTimeout(t)
    }
    if (fase === 'prep') {
      beep(1000, 250)
      setFase('lavoro')
      setRimanenti(durata)
      return
    }
    if (!giaCompletata.current) {
      giaCompletata.current = true
      beep(1200, 500)
      setFase('fatta')
      if (rifCompletata.current) rifCompletata.current(durata)
    }
  }, [rimanenti, fase, durata, beep])

  // Schermo acceso: la serie dura quanto dura, e il telefono è a terra.
  useEffect(() => {
    let sentinella = null
    if (fase !== 'fatta' && 'wakeLock' in navigator) {
      navigator.wakeLock.request('screen').then(s => { sentinella = s }).catch(() => {})
    }
    return () => { if (sentinella) sentinella.release().catch(() => {}) }
  }, [fase])

  const colore = fase === 'prep' ? COLORI.prep : COLORI.lavoro
  const ultimiSecondi = rimanenti <= 3 && rimanenti > 0 && fase !== 'fatta'

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000, background: 'var(--bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      {fase === 'fatta' ? (
        <>
          <div style={{ fontSize: 60, marginBottom: 10 }}>✅</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 22, marginBottom: 6, textAlign: 'center' }}>
            Serie {serieNumero} registrata
          </div>
          <div style={{ fontSize: 14, color: 'var(--muted)', textAlign: 'center' }}>
            {nomeEsercizio} · {durata}s
          </div>
        </>
      ) : (
        <>
          <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>
            Serie {serieNumero}{seriePreviste ? ` di ${seriePreviste}` : ''}
          </div>
          <div style={{
            width: '100%', maxWidth: 560, borderRadius: 20, padding: '24px 20px', textAlign: 'center',
            background: colore.bg,
            border: `2px solid ${ultimiSecondi ? colore.testo : colore.bordo}`,
            transition: 'border-color 0.15s',
          }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, letterSpacing: 1.5, color: colore.testo, textTransform: 'uppercase' }}>
              {fase === 'prep' ? 'Preparati' : 'Lavoro'}
            </div>
            <div style={{
              fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600,
              fontSize: 'clamp(76px, min(32vw, 26vh), 200px)',
              lineHeight: 1, color: colore.testo, margin: '10px 0',
              fontVariantNumeric: 'tabular-nums',
              opacity: ultimiSecondi && rimanenti % 2 === 0 ? 0.45 : 1,
              transition: 'opacity 0.12s',
            }}>
              {formattaTempo(rimanenti)}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{nomeEsercizio}</div>
          </div>

          <button onClick={onAnnulla}
            style={{ marginTop: 22, background: 'none', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 24px', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' }}>
            Annulla serie
          </button>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, textAlign: 'center', maxWidth: 320, lineHeight: 1.5 }}>
            Annullando non viene registrato nulla.
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Timer di solo recupero, usato dal formato Classico dopo aver salvato una serie.
 * Non ha una scaletta: è un singolo countdown con la durata di riposo del blocco.
 */
export function TimerRecupero({ secondi, onFine, onChiudi }) {
  const beep = useBeep()
  const [rimanenti, setRimanenti] = useState(secondi)

  useEffect(() => {
    if (rimanenti <= 0) { beep(1200, 400); onFine && onFine(); return }
    const t = setTimeout(() => {
      if (rimanenti <= 4) beep(660, 90)
      setRimanenti(r => r - 1)
    }, 1000)
    return () => clearTimeout(t)
  }, [rimanenti, beep, onFine])

  // Anche il recupero va letto a distanza: numeri grandi e lampeggio finale,
  // come nel timer a schermo intero.
  const ultimiSecondi = rimanenti <= 3 && rimanenti > 0

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      background: 'rgba(120,150,255,0.12)',
      border: `1px solid ${ultimiSecondi ? 'var(--accent2)' : 'rgba(120,150,255,0.4)'}`,
      borderRadius: 14, padding: '14px 16px', marginTop: 8,
      transition: 'border-color 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span style={{ fontSize: 22, flexShrink: 0 }}>⏸️</span>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600,
            fontSize: 'clamp(38px, 13vw, 56px)',
            color: 'var(--accent2)', lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            opacity: ultimiSecondi && rimanenti % 2 === 0 ? 0.45 : 1,
            transition: 'opacity 0.12s',
          }}>{formattaTempo(rimanenti)}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3, letterSpacing: 1, fontWeight: 700 }}>RECUPERO</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        <button onClick={() => setRimanenti(r => r + 30)}
          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--text)', cursor: 'pointer', fontWeight: 700 }}>+30s</button>
        <button onClick={onChiudi}
          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>Salta</button>
      </div>
    </div>
  )
}
